import KTComponent from '../component';
import { KTDatepickerStateManager } from './config';
import {
	DatePickerSegment,
	KTDatepickerConfigInterface,
	SelectedDateInterface,
} from './types';

export class KTDatepickerCalendar extends KTComponent {
	protected override readonly _name: string = 'datepicker-calendar';
	protected override readonly _config: KTDatepickerConfigInterface;

	private _calendarState: Date = new Date();

	private _state: KTDatepickerStateManager;

	private _timeInputDisplayElement: HTMLElement;
	private _calendarElement: HTMLElement;
	private _dropdownContainer: HTMLElement;

	private _dropdownElement: HTMLElement | null = null;
	private _mainDisplayElement: HTMLElement | null = null;
	private _isOpen: boolean = false;

	constructor(element: HTMLElement, state?: KTDatepickerStateManager) {
		super();
		this._state = state;

		this._init(element);
		this._buildConfig(this._state.getConfig());

		this._attachCalendarEventListeners();
		this._attachMonthYearEventListeners();
	}

	/**
	 * ========================================================================
	 * Date utils
	 * ========================================================================
	 */

	private _getDaysInMonth(year: number, month: number): number {
		return new Date(year, month + 1, 0).getDate();
	}

	private _getFirstDayOfMonth(year: number, month: number): number {
		const firstDayOfWeek = this._state.getFirstDayOfWeek();
		return (new Date(year, month, 1).getDay() - firstDayOfWeek + 7) % 7;
	}

	private _getYear(date: Date = new Date()): number {
		return date.getFullYear();
	}

	private _getMonth(date: Date = new Date()): number {
		return date.getMonth();
	}

	private _getMonthYear(year: number, month: number, offset: number) {
		const currentMonth = (month + offset) % 12;
		const currentYear = month + offset > 11 ? year + 1 : year;
		return { currentMonth, currentYear };
	}

	private _getMonthName(month: number): string {
		const monthNames = this._state.getMonthNames();
		return monthNames[month] || monthNames[0];
	}

	private _setMonth(month: number): void {
		this._calendarState.setMonth(month);
	}

	private _setYear(year: number): void {
		this._calendarState.setFullYear(year);
	}

	/**
	 * ========================================================================
	 * Calendar UI
	 * ========================================================================
	 */

	private _renderCalendar(selectedData?: Date): void {
		this._calendarState = selectedData ?? this._calendarState;
		const year = this._getYear(this._calendarState);
		const month = this._getMonth(this._calendarState);

		const calendarHtml = this._generateCalendarHtml(year, month);

		if (!this._dropdownContainer) {
			this._dropdownContainer = this._state
				.getTemplates()
				?.dropdownContainer(this._state);
			this._dropdownContainer.appendChild(calendarHtml);
		}

		if (this._calendarElement) {
			this._calendarElement.replaceWith(calendarHtml);
		} else {
			this._element.appendChild(this._dropdownContainer);
		}

		this._calendarElement = calendarHtml;

		this._initDropdown();

		if (this._config.enableTime) {
			this._renderTimeInputDisplay(this._state.getSelectedDateInterface());
			this._attachTimeSelectorEvent();
		}
	}

	private _generateCalendarHtml(year: number, month: number): HTMLElement {
		const calendar = this._state
			.getTemplates()
			?.calendar(this._state, this._config.visibleMonths);

		// Iterate over visible months and generate content for each
		for (let i = 0; i < this._config.visibleMonths; i++) {
			const { currentMonth, currentYear } = this._getMonthYear(year, month, i);
			const firstDayOfMonth = this._getFirstDayOfMonth(
				currentYear,
				currentMonth
			);
			const daysInMonth = this._getDaysInMonth(currentYear, currentMonth);

			const weeksHtml = this._generateWeeksHtml(
				currentYear,
				currentMonth,
				firstDayOfMonth,
				daysInMonth
			);
			const daysOfWeekHtml = this._state
				.getTemplates()
				?.daysOfWeek(this._state, this._state.getWeekdays()).innerHTML;

			this._updateMonthContainer(
				calendar,
				i,
				currentMonth,
				currentYear,
				daysOfWeekHtml,
				weeksHtml
			);
		}

		return calendar;
	}

	private _updateMonthContainer(
		calendar: HTMLElement,
		index: number,
		month: number,
		year: number,
		daysOfWeekHtml: string,
		weeksHtml: string[]
	): void {
		const monthContainer = calendar.querySelectorAll<HTMLElement>(
			`[${this._config.monthContainerSelector}]`
		)[index];
		const monthDisplay = monthContainer.querySelector<HTMLElement>(
			`[${this._config.monthDisplaySelector}]`
		);
		const yearDisplay = monthContainer.querySelector<HTMLElement>(
			`[${this._config.yearDisplaySelector}]`
		);
		const daysOfWeek = monthContainer.querySelector<HTMLElement>(
			`[${this._config.daysOfWeekSelector}]`
		);
		const datepickerGrid = monthContainer.querySelector<HTMLElement>(
			`[${this._config.calendarGridSelector}]`
		);

		if (monthDisplay) monthDisplay.textContent = this._getMonthName(month);
		if (yearDisplay) yearDisplay.textContent = year.toString();
		if (daysOfWeek) daysOfWeek.innerHTML = daysOfWeekHtml;
		if (datepickerGrid) datepickerGrid.innerHTML = weeksHtml.join('');
	}

	private _generateWeeksHtml(
		year: number,
		month: number,
		firstDayOfMonth: number,
		daysInMonth: number
	): string[] {
		const weeksHtml = [];
		let weekHtml = [];

		const totalCells = this._calculateTotalCells(firstDayOfMonth, daysInMonth);

		for (let i = 0; i < totalCells; i++) {
			const dayOfMonth = i - firstDayOfMonth + 1;
			let date = this._adjustDateForEdges(year, month, dayOfMonth);

			// Check if the day is disabled, which includes edge days that should be disabled
			const isDisabled = this._isDateDisabled(date, dayOfMonth, daysInMonth);
			const dayCellHtml = this._generateDayCellHtml(date, isDisabled);
			weekHtml.push(dayCellHtml);

			if ((i + 1) % 7 === 0) {
				weeksHtml.push(
					this._state.getTemplates()?.week(this._state, weekHtml).outerHTML
				);
				weekHtml = [];
			}
		}

		return weeksHtml;
	}

	private _calculateTotalCells(
		firstDayOfMonth: number,
		daysInMonth: number
	): number {
		const totalDays = daysInMonth + firstDayOfMonth;
		return Math.ceil(totalDays / 7) * 7;
	}

	private _adjustDateForEdges(
		year: number,
		month: number,
		dayOfMonth: number
	): Date {
		if (dayOfMonth <= 0) {
			const prevMonth = month === 0 ? 11 : month - 1;
			const prevYear = month === 0 ? year - 1 : year;
			const daysInPrevMonth = this._getDaysInMonth(prevYear, prevMonth);
			return new Date(prevYear, prevMonth, daysInPrevMonth + dayOfMonth);
		} else if (dayOfMonth > this._getDaysInMonth(year, month)) {
			const nextMonth = month === 11 ? 0 : month + 1;
			const nextYear = month === 11 ? year + 1 : year;
			return new Date(
				nextYear,
				nextMonth,
				dayOfMonth - this._getDaysInMonth(year, month)
			);
		}
		return new Date(year, month, dayOfMonth);
	}

	private _isDateDisabled(
		date: Date,
		dayOfMonth: number,
		daysInMonth: number
	): boolean {
		// Determine if the date is from a previous or next month (edge days)
		const isEdgeDay = dayOfMonth <= 0 || dayOfMonth > daysInMonth;

		// Use _isOutsideVisibleRange and _isMinMaxConfigRange to determine if the date should be disabled
		const isDisabled =
			isEdgeDay ||
			this._isOutsideVisibleRange(date) ||
			this._state.isMinMaxConfigRange(date);

		return isDisabled;
	}

	private _generateDayCellHtml(date: Date, isDisabled: boolean): string {
		const isSelected =
			!this._config.range &&
			this._state.getSelectedDate()?.toDateString() === date.toDateString();
		const isToday = date.toDateString() === new Date().toDateString();
		const isInRange = this._isSelectedCalendarDateInRange(date);
		const isStartDate =
			this._state.getSelectedDateRange()?.startDate?.toDateString() ===
			date.toDateString();
		const isEndDate =
			this._state.getSelectedDateRange()?.endDate?.toDateString() ===
			date.toDateString();

		const dayCell = this._state
			.getTemplates()
			?.dayCell(
				this._state,
				date,
				isSelected,
				isToday,
				isDisabled,
				isInRange,
				isStartDate,
				isEndDate
			);

		if (isToday && !isDisabled) {
			dayCell.focus();
		}

		return dayCell.outerHTML;
	}

	private _isOutsideVisibleRange(date: Date): boolean {
		const dateYear = date.getFullYear();
		const dateMonth = date.getMonth();
		const firstVisibleMonth = this._calendarState.getMonth();
		let lastVisibleMonth =
			(firstVisibleMonth + this._config.visibleMonths - 1) % 12;

		let lastVisibleYear = this._calendarState.getFullYear();
		if (lastVisibleMonth < firstVisibleMonth) {
			lastVisibleYear++;
		}

		return (
			dateYear < this._calendarState.getFullYear() || // Previous year
			dateYear > lastVisibleYear || // Next year
			(dateYear === this._calendarState.getFullYear() &&
				dateMonth < firstVisibleMonth) || // Current year, before first visible month
			(dateYear === lastVisibleYear && dateMonth > lastVisibleMonth) || // Last visible year, after last visible month
			(dateYear === lastVisibleYear &&
				dateMonth === lastVisibleMonth &&
				date.getDate() > this._getDaysInMonth(dateYear, dateMonth)) // Last visible month, days exceeding the month's length
		);
	}

	private _isSelectedCalendarDateInRange(date: Date): boolean {
		const { startDate: startDate, endDate: endDate } =
			this._state.getSelectedDateRange() || {};
		if (startDate && endDate) {
			// Create new Date objects with time components set to 0 for accurate comparison
			const start = new Date(
				startDate.getFullYear(),
				startDate.getMonth(),
				startDate.getDate()
			);
			const end = new Date(
				endDate.getFullYear(),
				endDate.getMonth(),
				endDate.getDate()
			);
			const current = new Date(
				date.getFullYear(),
				date.getMonth(),
				date.getDate()
			);

			return current >= start && current <= end;
		}
		return false;
	}

	private _attachCalendarEventListeners(): void {
		document.addEventListener('keydown', (event) => {
			if (
				(event.key === 'Escape' || event.key === 'Enter') &&
				this._isMonthYearSelectorVisible()
			) {
				this._handleToggleMonthYearSelector();
			}
		});

		document.addEventListener('click', (event) => {
			const target = event.target as HTMLElement;

			if (
				this._isMonthYearSelectorVisible() &&
				!target.closest(`[${this._config.monthYearDisplaySelector}]`) &&
				!target.closest(`[${this._config.monthYearSelector}]`)
			) {
				this._handleToggleMonthYearSelector();
			}
		});

		this._element.addEventListener('click', (event) => {
			const target = event.target as HTMLElement;

			// Previous month button
			if (target.closest(`[${this._config.prevBtnSelector}]`)) {
				this._goToCalendarPreviousMonth();
			}

			// Next month button
			else if (target.closest(`[${this._config.nextBtnSelector}]`)) {
				this._goToCalendarNextMonth();
			}

			// Month/year display
			else if (target.closest(`[${this._config.monthYearDisplaySelector}]`)) {
				this._renderMonthYearSelector();
			}

			// Day cells
			else if (
				target.closest(
					`[${this._config.dateCellSelector}]:not([data-disabled="true"])`
				)
			) {
				this._handleCalendarDayClick(target);
			}
		});

		this._element.addEventListener(
			this._config.keyboardNavigateEvent,
			this._handleOnDateChange.bind(this)
		);

		this._element.addEventListener('mouseover', (event) => {
			const target = event.target as HTMLElement;
			const dateCell = target.closest(
				`[${this._config.dateCellSelector}]:not([data-disabled="true"])`
			) as HTMLElement;

			if (dateCell && this._state.getSelectedDateRange()?.startDate) {
				const day = parseInt(dateCell.dataset.day!, 10);
				const month = parseInt(dateCell.dataset.month!, 10);
				const year = parseInt(dateCell.dataset.year!, 10);
				const hoveredDate = new Date(year, month, day);

				this._updateCalendarHover(hoveredDate);
			}
		});
	}

	private _handleOnDateChange(event: CustomEvent) {
		const { selectedDate, selectedDateRange } = event.detail
			.payload as SelectedDateInterface;

		if (selectedDate) {
			this._state.setSelectedDate(selectedDate);
		} else if (selectedDateRange && this._config.range) {
			this._state.setSelectedDateRange(
				selectedDateRange.startDate,
				selectedDateRange.endDate
			);
		}

		this._renderCalendar(selectedDate);
	}

	private _goToCalendarPreviousMonth(): void {
		const month = this._calendarState.getMonth() - 1;

		this._calendarState.setMonth(month);
		this._renderCalendar();

		this._dispatchEvent(
			this._config.calendarPreviousMonthEvent,
			this._state.getSelectedDateInterface()
		);
		this._fireEvent(
			this._config.calendarPreviousMonthEvent,
			this._state.getSelectedDateInterface()
		);
	}

	private _goToCalendarNextMonth(): void {
		const month = this._calendarState.getMonth() + 1;

		this._calendarState.setMonth(month);
		this._renderCalendar();

		this._dispatchEvent(
			this._config.calendarNextMonthEvent,
			this._state.getSelectedDateInterface()
		);
		this._fireEvent(
			this._config.calendarNextMonthEvent,
			this._state.getSelectedDateInterface()
		);
	}

	private _handleCalendarDayClick(cell: HTMLElement): void {
		const day = parseInt(cell.dataset.day!, 10);
		const month = parseInt(cell.dataset.month!, 10);
		const year = parseInt(cell.dataset.year!, 10);

		const newSelectedDate = new Date(
			year,
			month,
			day,
			this._calendarState.getHours(),
			this._calendarState.getMinutes(),
			this._calendarState.getSeconds()
		);

		if (this._config.range) {
			const selectedDateRange = this._state.getSelectedDateRange();

			if (!selectedDateRange.startDate) {
				this._state.setSelectedDateRange(newSelectedDate);
			} else if (!selectedDateRange.endDate) {
				const startDate = selectedDateRange.startDate;
				this._state.setSelectedDateRange(
					newSelectedDate < startDate ? newSelectedDate : startDate,
					newSelectedDate > startDate ? newSelectedDate : startDate
				);
			} else {
				// If both start and end dates are already selected, reset the range with the new date as the start date
				this._state.setSelectedDateRange(newSelectedDate);
			}
		} else {
			this._state.setSelectedDate(newSelectedDate);
		}

		this._renderCalendar();

		this._dispatchEvent(
			this._config.dateChangeEvent,
			this._state.getSelectedDateInterface()
		);
		this._fireEvent(
			this._config.dateChangeEvent,
			this._state.getSelectedDateInterface()
		);

		this._dispatchEvent(
			this._config.calendarRenderedEvent,
			this._state.getSelectedDateInterface()
		);
		this._fireEvent(
			this._config.calendarRenderedEvent,
			this._state.getSelectedDateInterface()
		);
	}

	private _updateCalendarHover(hoveredDate: Date): void {
		const { startDate: selectedStartDate, endDate: selectedEndDate } =
			this._state.getSelectedDateRange();

		// Find the date cell element based on hoveredDate
		const hoveredCell = this._element.querySelector(
			`[${
				this._config.dateCellSelector
			}][data-year="${hoveredDate.getFullYear()}"][data-month="${hoveredDate.getMonth()}"][data-day="${hoveredDate.getDate()}"]`
		);

		if (!hoveredCell || !selectedStartDate || selectedEndDate) {
			return;
		}

		// Clear previous hover classes
		const allCells = this._element.querySelectorAll(
			`[${this._config.dateCellSelector}]`
		);
		allCells.forEach((cell) =>
			cell.classList.remove(this._config.hoveredInRangeClass)
		);

		// Set time components to zero for both dates to compare only the dates (once)
		hoveredDate.setHours(0, 0, 0, 0);
		selectedStartDate.setHours(0, 0, 0, 0);

		const startDateTimestamp = selectedStartDate.getTime();
		const hoveredDateTimestamp = hoveredDate.getTime();

		// Calculate lowerBound and upperBound only once
		const lowerBound = Math.min(startDateTimestamp, hoveredDateTimestamp);
		const upperBound = Math.max(startDateTimestamp, hoveredDateTimestamp);

		allCells.forEach((cell) => {
			if (cell instanceof HTMLElement) {
				const cellDate = new Date(
					parseInt(cell.dataset.year!),
					parseInt(cell.dataset.month!),
					parseInt(cell.dataset.day!)
				);
				cellDate.setHours(0, 0, 0, 0); // Set time to midnight
				const cellTimestamp = cellDate.getTime();

				if (cellTimestamp >= lowerBound && cellTimestamp <= upperBound) {
					cell.classList.add(this._config.hoveredInRangeClass);
				}
			}
		});
	}

	/**
	 * ========================================================================
	 * Month/Year UI
	 * ========================================================================
	 */

	private _renderMonthYearSelector(): void {
		const monthYearElement = this._state
			.getTemplates()
			?.monthYearSelector(this._state);
		this._clearAndAppend(monthYearElement); // Clear existing content and append the new selector

		this._populateMonthYearOptions(monthYearElement); // Populate month and year options
	}

	private _clearAndAppend(newContent: HTMLElement | null): void {
		this._clearMonthYearSelector();
		if (newContent) {
			this._element.appendChild(newContent);
		}
	}

	private _populateMonthOptions(
		monthYearElement: HTMLElement,
		selectedMonthIndex: number
	) {
		const monthNames = this._state.getMonthNames();
		const monthOptionsHtml = this._state
			.getTemplates()
			?.monthOptions(this._state, monthNames, selectedMonthIndex);
		const monthOptionsContainer = monthYearElement.querySelector(
			`[${this._config.monthOptionsSelector}]`
		);
		monthOptionsContainer?.insertAdjacentHTML('beforeend', monthOptionsHtml);
	}

	private _populateYearOptions(
		monthYearElement: HTMLElement,
		selectedYear: number,
		yearLength: number
	) {
		const yearOptionsHtml = this._state
			.getTemplates()
			?.yearOptions(this._state, selectedYear, yearLength);
		const yearOptionsContainer = monthYearElement.querySelector(
			`[${this._config.yearOptionSelector}]`
		);
		yearOptionsContainer?.insertAdjacentHTML('beforeend', yearOptionsHtml);
	}

	private _populateMonthYearOptions(monthYearElement: HTMLElement): void {
		const selectedYear = this._getYear(this._calendarState);
		const selectedMonthIndex = this._getMonth(this._calendarState);
		const yearLength = this._config.visibleYears;

		this._populateMonthOptions(monthYearElement, selectedMonthIndex);
		this._populateYearOptions(monthYearElement, selectedYear, yearLength);

		this._updateMonthYearDisplayTitles(
			monthYearElement,
			selectedMonthIndex,
			selectedYear
		);
	}

	private _isMonthYearSelectorVisible(): boolean {
		const monthYearElement = this._element.querySelector(
			`[${this._config.monthYearSelector}]`
		);
		return (
			monthYearElement &&
			window.getComputedStyle(monthYearElement).display !== 'none'
		);
	}

	private _attachMonthYearEventListeners(): void {
		// Attach the listener once to the main element (or a suitable parent)
		this._element.addEventListener('click', (event) => {
			const target = event.target as HTMLElement;

			// Check if the click is within the month/year selector
			const monthYearSelector = target.closest(
				`[${this._config.monthYearSelector}]`
			);
			if (!monthYearSelector) {
				return; // Ignore clicks outside the selector
			}

			// Year buttons
			if (target.closest(`[${this._config.yearOptionSelector}]`)) {
				this._handleYearClick(event);
			}

			// Month buttons
			else if (target.closest(`[${this._config.monthOptionSelector}]`)) {
				this._handleMonthClick(event);
			}

			// Month/year display (to close the selector)
			else if (target.closest(`[${this._config.monthYearDisplaySelector}]`)) {
				this._handleToggleMonthYearSelector();
			}
		});
	}

	private _updateMonthYearDisplayTitles(
		monthYearElement: HTMLElement,
		selectedMonthIndex: number,
		selectedYear: number
	): void {
		const monthDisplay = monthYearElement.querySelector(
			`[${this._config.monthDisplaySelector}]`
		);
		const yearDisplay = monthYearElement.querySelector(
			`[${this._config.yearDisplaySelector}]`
		);

		monthDisplay!.textContent = this._getMonthName(selectedMonthIndex);
		yearDisplay!.textContent = selectedYear.toString();
	}

	private _clearMonthYearSelector(): void {
		this._element
			.querySelector(`[${this._config.monthYearSelector}]`)
			?.remove();
	}

	private _handleMonthClick(event: MouseEvent): void {
		const button = event.target as HTMLButtonElement;
		const selectedMonth = parseInt(button.getAttribute('data-month')!, 10);

		this._setMonth(selectedMonth);

		const monthYearElement = this._element.querySelector(
			`[${this._config.monthYearSelector}]`
		) as HTMLElement;

		// Update the titles with the monthSelector, selectedMonth and current year
		this._updateMonthYearDisplayTitles(
			monthYearElement,
			selectedMonth,
			this._getYear(this._calendarState)
		);
	}

	private _handleYearClick(event: MouseEvent): void {
		const button = event.target as HTMLButtonElement;
		const selectedYear = parseInt(button.getAttribute('data-year')!, 10);

		this._setYear(selectedYear);

		const monthYearElement = this._element.querySelector(
			`[${this._config.monthYearSelector}]`
		) as HTMLElement;

		// Update the titles with the monthSelector, current month and selectedYear
		this._updateMonthYearDisplayTitles(
			monthYearElement,
			this._getMonth(this._calendarState),
			selectedYear
		);
	}

	private _handleToggleMonthYearSelector(): void {
		this._clearMonthYearSelector();
		this._renderCalendar();
	}

	/**
	 * ========================================================================
	 * Date Time input
	 * ========================================================================
	 */

	private _renderTimeInputDisplay(selectedDate: SelectedDateInterface): void {
		let newElement: HTMLElement | null = null;

		if (this._config.range && selectedDate.selectedDateRange) {
			const { startDate, endDate } = selectedDate.selectedDateRange;
			const startDateTimeDisplay =
				this._state.getDateDisplayInterface(startDate);
			const endDateTimeDisplay = this._state.getDateDisplayInterface(endDate);
			newElement = this._state
				.getTemplates()
				?.timeSelectorRangeDisplay(
					this._state,
					startDateTimeDisplay,
					endDateTimeDisplay,
					this._config.hourStep,
					this._config.minuteStep,
					this._config.secondStep,
					this._state.getDisabledHours(),
					this._state.getDisabledMinutes(),
					this._state.is12Hours(),
					selectedDate
				);
		} else {
			const dateTimeDisplay = this._state.getDateDisplayInterface(
				selectedDate.selectedDate
			);
			newElement = this._state
				.getTemplates()
				?.timeSelectorDisplay(
					this._state,
					dateTimeDisplay,
					this._config.hourStep,
					this._config.minuteStep,
					this._config.secondStep,
					this._state.getDisabledHours(),
					this._state.getDisabledMinutes(),
					this._state.is12Hours(),
					selectedDate.selectedDate
				);
		}

		this._calendarElement.appendChild(newElement);

		this._timeInputDisplayElement = newElement;
	}

	private _attachTimeSelectorEvent(): void {
		if (this._timeInputDisplayElement) {
			this._timeInputDisplayElement.addEventListener(
				'change',
				(event: Event) => {
					const changedElement = event.target as HTMLSelectElement;

					// Check if the change event is from a time selector
					const timeSegment = changedElement.dataset.datepickerSegment as
						| DatePickerSegment
						| undefined;

					if (timeSegment) {
						const newValue = parseInt(changedElement.value, 10);

						if (this._config.range) {
							const isStartTimeSelector =
								changedElement.closest(
									`[data-kt-datepicker-time-type="start"]`
								) !== null;
							const targetDate =
								this._getTargetDateForRange(isStartTimeSelector);

							if (targetDate) {
								this._updateTimeComponent(targetDate, timeSegment, newValue);
								this._state.setSelectedDate(targetDate);
							}
						} else {
							this._updateTimeComponent(
								this._state.getSelectedDate(),
								timeSegment,
								newValue
							);
							this._state.setSelectedDate(this._state.getSelectedDate());
						}

						this._dispatchEvent(
							this._config.dateChangeEvent,
							this._state.getSelectedDateInterface()
						);
						this._fireEvent(
							this._config.dateChangeEvent,
							this._state.getSelectedDateInterface()
						);
					}
				}
			);
		}
	}

	private _updateTimeComponent(
		dateToUpdate: Date,
		timeSegment: DatePickerSegment,
		newValue: number
	): void {
		if (!dateToUpdate) {
			return;
		}

		switch (timeSegment) {
			case DatePickerSegment.Hours:
			case DatePickerSegment.EndHours:
				// Handle 12-hour format conversion if needed
				if (!this._state.is12Hours()) {
					const currentHour = dateToUpdate.getHours();
					const isPM = currentHour >= 12;
					newValue = newValue === 12 ? 0 : newValue; // 12 AM is 0 in 24-hour format
					newValue = isPM ? newValue + 12 : newValue; // Add 12 for PM hours
				}
				dateToUpdate.setHours(newValue);
				break;
			case DatePickerSegment.Minutes:
			case DatePickerSegment.EndMinutes:
				dateToUpdate.setMinutes(newValue);
				break;
			case DatePickerSegment.Seconds:
			case DatePickerSegment.EndSeconds:
				dateToUpdate.setSeconds(newValue);
				break;
			case DatePickerSegment.AmPm:
			case DatePickerSegment.EndAmPm:
				const currentHour = dateToUpdate.getHours();
				const newHour =
					newValue === 12 ? (currentHour % 12) + 12 : currentHour % 12;
				dateToUpdate.setHours(newHour);
				break;
		}
	}

	private _getTargetDateForRange(isStartTimeSelector: boolean): Date | null {
		return isStartTimeSelector
			? this._state.getSelectedDateRange()?.startDate
			: this._state.getSelectedDateRange()?.endDate;
	}

	/**
	 * ========================================================================
	 * Dropdown UI
	 * ========================================================================
	 */

	private _initDropdown(): void {
		this._dropdownElement = this._element.querySelector(
			`[${this._config.dropdownSelector}]`
		);
		this._mainDisplayElement = this._element.querySelector(
			`[${this._config.mainDisplaySelector}]`
		);

		if (this._dropdownElement) {
			// Remove existing click event
			this._mainDisplayElement.removeEventListener(
				'click',
				this._toggleDropdown.bind(this)
			);

			// Reattach the click event
			this._mainDisplayElement.addEventListener(
				'click',
				this._toggleDropdown.bind(this)
			);
		}
	}

	private _toggleDropdown() {
		console.log('toggleDropdown');
		this._isOpen ? this._closeDropdown() : this._openDropdown();
	}

	private _openDropdown() {
		if (this._isOpen) return;
		this._dropdownElement.classList.remove('hidden');
		this._isOpen = true;
		this._setAriaAttributes(); // Update ARIA attributes when opened

		this._dispatchEvent('show.datepicker');
		this._fireEvent('show.datepicker');
	}

	private _closeDropdown() {
		if (!this._isOpen) return;
		this._dropdownElement.classList.add('hidden');
		this._isOpen = false;
		this._setAriaAttributes(); // Update ARIA attributes when closed

		this._dispatchEvent('close.datepicker');
		this._fireEvent('close.datepicker');
	}

	private _setAriaAttributes() {
		this._dropdownElement.setAttribute(
			'aria-expanded',
			this._isOpen.toString()
		);
	}

	/**
	 * ========================================================================
	 * Public API
	 * ========================================================================
	 */

	public renderCalendar(): void {
		this._renderCalendar();
	}
}
